import React, { memo, useEffect, useMemo } from "react";
import { InvoiceGrid } from "./styled-components";
import { useTableGlobalInvoicesConfig } from "./hooks";
import { formatNumber } from "helpers/getNumberFormats";
import {
  Checkbox,
  FormControlLabel,
  Grid,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { WarningAmber } from "@mui/icons-material";
import { useForm } from "hooks/useForm";
import { clampQuantity, clampUnitPrice, paymentFieldKey } from "./invoiceTotals";
import { isInvalidQuantity, MIN_INVOICE_QUANTITY } from "./validateQuantity";
import { accordionSx } from "./styles";

// PU y Litros nunca son negativos: se quita el "-" antes de tocar el valor,
// no se corrige despues. Dejarlo pasar y recortarlo luego (clampUnitPrice/
// clampQuantity) alcanzaba a mostrar el signo mientras el usuario tecleaba.
const stripNegativeSign = (value) => value.replace(/-/g, "");

const truncateDecimals = (value, maxDecimals = 6) => {
  const raw = stripNegativeSign(value).replace(/[^\d.]/g, "");
  const [int = "", dec = ""] = raw.split(".");
  return dec ? `${int}.${dec.slice(0, maxDecimals)}` : int;
};

/**
 * Renglones de metodo de pago dentro de una tasa. La casilla Manual habilita
 * PU y Litros; Subtotal y Total siempre son de solo lectura porque los
 * recalcula el reducer.
 */
export const InvoiceAccordionDetails = memo(
  ({ data, selectedPayments, onTogglePayment, originalPayments = {} }) => {
    const {
      handlePaymentUnitPrice,
      handlePaymentQuantity,
    } = useTableGlobalInvoicesConfig();
    const { payments, taxRate, creLicense } = data;

    const initialValues = useMemo(() => {
      if (!payments) return {};
      return Object.keys(payments).reduce((acc, key) => {
        acc[`pu_${key}`] = payments[key]?.unitPrice ?? 0;
        acc[`qty_${key}`] = payments[key]?.quantity ?? 0;
        return acc;
      }, {});
    }, [payments]);

    const { values: formValues, onChangeNumber, setValues } = useForm(
      initialValues,
    );

    // Los valores originales llegan por prop, congelados una sola vez por
    // busqueda en el hook padre -no en un ref local-: este componente vive
    // dentro del Collapse del permiso y se desmonta al colapsarlo, asi que
    // un ref propio se recalcularia leyendo el store ya editado al volver a
    // expandirlo, en vez del valor real que trajo el backend.
    useEffect(() => {
      if (!payments) return;

      Object.keys(payments).forEach((key) => {
        const fieldKey = paymentFieldKey(creLicense, taxRate, key);
        const isChecked = selectedPayments?.[fieldKey] || false;
        if (isChecked) return;

        const originalUP = originalPayments[fieldKey]?.unitPrice ?? 0;
        const originalQty = originalPayments[fieldKey]?.quantity ?? 0;
        const updates = {};

        if (formValues[`pu_${key}`] !== originalUP) {
          updates[`pu_${key}`] = originalUP;
          handlePaymentUnitPrice(creLicense, taxRate, key, originalUP);
        }
        if (formValues[`qty_${key}`] !== originalQty) {
          updates[`qty_${key}`] = originalQty;
          handlePaymentQuantity(creLicense, taxRate, key, originalQty);
        }
        if (Object.keys(updates).length) setValues(updates);
      });
    }, [selectedPayments]);

    if (!payments) return null;

    return (
      <>
        {Object.keys(payments).map((key) => {
          const payment = payments[key];
          const { name, unitPrice } = payment;
          const fieldKey = paymentFieldKey(creLicense, taxRate, key);
          const isChecked = selectedPayments?.[fieldKey] || false;
          const isBlocked = !!payment?.availability?.blocked;
          // Sin idPayment (catalogo /payments no cargado al buscar) el renglon
          // no se puede timbrar: no se habilita Manual.
          const hasPaymentId = payment?.idPayment != null;
          // Un metodo sin tocar (0 Lt) no es una partida invalida, solo esta
          // vacio: fillPaymentGroups siempre completa los 5 metodos de pago
          // (CRM-3022 solo aplica a partidas con cantidad real).
          const rawQuantity = Number(formValues[`qty_${key}`] ?? payment?.quantity) || 0;
          const hasInvalidQuantity = rawQuantity > 0 && isInvalidQuantity(rawQuantity);

          return (
            <Grid
              container
              spacing={1}
              p={2}
              key={key}
              sx={accordionSx.box}
              style={{ padding: 12 }}
            >
              <InvoiceGrid
                item
                xs={12}
                sm={onTogglePayment ? 1 : 2}
                style={{ display: "flex", alignItems: "center", gap: 4 }}
              >
                {isBlocked && (
                  <Tooltip title={payment.availability.message}>
                    <WarningAmber color="warning" fontSize="small" />
                  </Tooltip>
                )}
                <Typography>{name}</Typography>
              </InvoiceGrid>

              {onTogglePayment && (
                <InvoiceGrid
                  item
                  xs={12}
                  sm={1}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={isChecked}
                        disabled={!hasPaymentId}
                        onChange={() => onTogglePayment(creLicense, taxRate, key)}
                        size="small"
                      />
                    }
                    label="Manual"
                    labelPlacement="start"
                  />
                </InvoiceGrid>
              )}

              <InvoiceGrid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="text"
                  label="PU"
                  name={`pu_${key}`}
                  value={formValues[`pu_${key}`] ?? unitPrice}
                  disabled={!isChecked}
                  onChange={(e) => {
                    // PU nunca es negativo: se quita el "-" antes de que
                    // onChangeNumber lo formatee, para que ni siquiera se
                    // alcance a mostrar (antes se dejaba teclear y recien se
                    // corregia despues, mostrando el signo un instante).
                    e.target.value = stripNegativeSign(e.target.value);
                    onChangeNumber(e, { decimals: 6 });
                    const typed = parseFloat(truncateDecimals(e.target.value)) || 0;
                    const clamped = clampUnitPrice(typed);
                    handlePaymentUnitPrice(creLicense, taxRate, key, clamped);
                    // Si el valor se tuvo que ajustar (ej. por arriba del
                    // maximo), el input debe mostrar lo mismo que se va a
                    // timbrar -antes se quedaba mostrando el valor crudo
                    // mientras el store ya habia aplicado el limite.
                    if (clamped !== typed) setValues({ [`pu_${key}`]: clamped });
                  }}
                  variant="outlined"
                  InputProps={{
                    inputProps: { min: 0, step: "any" },
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">MXN</InputAdornment>
                    ),
                  }}
                />
              </InvoiceGrid>

              <InvoiceGrid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="text"
                  label="Litros"
                  name={`qty_${key}`}
                  value={formValues[`qty_${key}`] ?? payment?.quantity}
                  disabled={!isChecked}
                  error={hasInvalidQuantity}
                  helperText={hasInvalidQuantity ? `Mínimo ${MIN_INVOICE_QUANTITY} Lt` : ""}
                  onChange={(e) => {
                    // Litros tampoco es negativo; mismo criterio que PU.
                    e.target.value = stripNegativeSign(e.target.value);
                    onChangeNumber(e, { decimals: 6 });
                    const typed = parseFloat(truncateDecimals(e.target.value)) || 0;
                    const clamped = clampQuantity(typed);
                    handlePaymentQuantity(creLicense, taxRate, key, clamped);
                    if (clamped !== typed) setValues({ [`qty_${key}`]: clamped });
                  }}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">Lt</InputAdornment>
                    ),
                  }}
                />
              </InvoiceGrid>

              <InvoiceGrid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="text"
                  label="Subtotal"
                  value={formatNumber({ input: payment?.subtotal, decimals: 2 })}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">MXN</InputAdornment>
                    ),
                  }}
                  disabled
                />
              </InvoiceGrid>

              <InvoiceGrid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="text"
                  label="Total"
                  value={formatNumber({ input: payment?.total, decimals: 2 })}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">MXN</InputAdornment>
                    ),
                  }}
                  disabled
                />
              </InvoiceGrid>
            </Grid>
          );
        })}
      </>
    );
  },
);
